/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Component, Fragment } from 'react'
import { injectIntl } from 'react-intl'

import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiLoadingSpinner,
  EuiSpacer,
  EuiText,
  EuiFormHelpText,
  EuiSwitch,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { AsyncRequestState } from '@modules/ui-types'
import { CuiAlert } from '@modules/cui/Alert'

import type { IntlShape } from 'react-intl'

export interface Props {
  organizationId: string
  enabled: boolean
  setDiskNotifications: (enabled: boolean) => void
  setDiskNotificationsRequest: AsyncRequestState
  resetSetDiskNotifications: () => void
  intl: IntlShape
}

class EnableDiskNotificationsButton extends Component<Props> {
  componentWillUnmount() {
    this.props.resetSetDiskNotifications()
  }

  render() {
    const { setDiskNotifications, setDiskNotificationsRequest, enabled, intl } = this.props

    return (
      <Fragment>
        <EuiFlexGroup gutterSize='m' alignItems='center' responsive={false}>
          <EuiFlexItem grow={false}>
            <EuiText size='s'>
              <strong>
                <FormattedMessage
                  id='enable-disk-notifications-button.label'
                  defaultMessage='Disk usage notifications'
                />
              </strong>
            </EuiText>

            <EuiFormHelpText>
              <FormattedMessage
                id='enable-disk-notifications-button.description'
                defaultMessage='Warning email when disk usage is above 90%'
              />
            </EuiFormHelpText>
          </EuiFlexItem>

          <EuiFlexItem grow={false}>
            <EuiSwitch
              checked={enabled}
              label={intl.formatMessage({
                id: 'enable-disk-notifications-switch.label',
                defaultMessage: 'Disk usage notifications toggle',
              })}
              showLabel={false}
              onChange={() => setDiskNotifications(!enabled)}
              disabled={setDiskNotificationsRequest.inProgress}
            />
          </EuiFlexItem>

          {setDiskNotificationsRequest.inProgress && (
            <EuiFlexItem grow={false}>
              <EuiLoadingSpinner size='m' />
            </EuiFlexItem>
          )}
        </EuiFlexGroup>

        {setDiskNotificationsRequest.error && (
          <Fragment>
            <EuiSpacer size='m' />

            <CuiAlert type='error'>{setDiskNotificationsRequest.error}</CuiAlert>
          </Fragment>
        )}
      </Fragment>
    )
  }
}

export default injectIntl(EnableDiskNotificationsButton)
